import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { getAgents, getAgentCategories, Agent } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import { useTheme } from '../context/ThemeContext';
import ConversationSidebar from '../components/ConversationSidebar';
import Header from '../components/Header';
import { CachedImage } from '../components/CachedImage';
import { preloadImages } from '../services/imageCache';

const AgentsPage: React.FC = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getAccessToken } = useAuth();
  const { isSidebarOpen, toggleSidebar } = useSidebar();
  const { colors, theme } = useTheme();
  const history = useHistory();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const fetchAgents = useCallback(async (category?: string | null, search?: string) => {
    try {
      setIsLoading(true);      
      await getAccessToken();

      const params: { category?: string; search?: string } = {};

      if (category) params.category = category;
      if (search) params.search = search;

      const loadedAgents = await getAgents(Object.keys(params).length > 0 ? params : undefined);

      setAgents(loadedAgents);
      preloadImages(loadedAgents.map(a => a.img_url));
    } catch (err) {
      console.error('Error loading agents:', err);
      setError('Failed to load agents');
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken]);

  // Initial load: fetch categories + all agents
  useEffect(() => {
    const init = async () => {
      try {
        await getAccessToken();
        const [loadedAgents, loadedCategories] = await Promise.all([
          getAgents(),
          getAgentCategories()
        ]);

        setAgents(loadedAgents);
        setCategories(loadedCategories);
        preloadImages(loadedAgents.map(a => a.img_url));
      } catch (err) {
        console.error('Error loading agents:', err);
        setError('Failed to load agents');
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, []);

  // Re-fetch when category changes
  useEffect(() => {
    // Skip initial render
    if (isLoading && agents.length === 0) 
      return;

    fetchAgents(selectedCategory, searchQuery);
  }, [selectedCategory]);

  // Debounced search
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (debounceTimer.current) 
      clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      fetchAgents(selectedCategory, value);
    }, 600);
  };

  const handleSelectConversation = (conversationId: string) => {
    if (conversationId === '') {
      history.push('/');
    } else {
      history.push('/', { conversationId });
    }
  };

  const handleAgentClick = (agent: Agent) => {
    if (agent.redirect_url) {
      window.open(agent.redirect_url, '_blank', 'noopener,noreferrer');
      return;
    }
    history.push('/', {
      agentData: {
        id: agent.id,
        title: agent.title,
        description: agent.description,
        img_url: agent.img_url,
        required_connection: agent.required_connection
      }
    });
  };

  const agentsByCategory = agents.reduce((acc, agent) => {
    const category = agent.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(agent);
    return acc;
  }, {} as Record<string, Agent[]>);

  const sortedCategories = Object.keys(agentsByCategory).sort((a, b) => {
    const minOrderA = Math.min(...agentsByCategory[a].map(agent => agent.sort_order));
    const minOrderB = Math.min(...agentsByCategory[b].map(agent => agent.sort_order));
    return minOrderA - minOrderB;
  });

  const AgentCard: React.FC<{ agent: Agent }> = ({ agent }) => (
    <div
      style={{
        backgroundColor: colors.surface,
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: theme === 'light' ? '0 2px 8px rgba(0, 0, 0, 0.06)' : '0 2px 8px rgba(0, 0, 0, 0.25)',
        border: `1px solid ${colors.border}`,
        cursor: agent.can_use ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        opacity: agent.can_use ? 1 : 0.45,
      }}
      onMouseEnter={(e) => {
        if (!agent.can_use) return;
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = theme === 'light' ? '0 12px 32px rgba(0, 0, 0, 0.12)' : '0 12px 32px rgba(0, 0, 0, 0.4)';
        e.currentTarget.style.borderColor = colors.borderHover;
      }}
      onMouseLeave={(e) => {
        if (!agent.can_use) return;
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = theme === 'light' ? '0 2px 8px rgba(0, 0, 0, 0.06)' : '0 2px 8px rgba(0, 0, 0, 0.25)';
        e.currentTarget.style.borderColor = colors.border;
      }}
      onClick={() => agent.can_use && handleAgentClick(agent)}
    >
      {/* Visual hero area */}
      <div style={{
        background: theme === 'light'
          ? 'linear-gradient(135deg, #f0f4ff 0%, #e8eeff 50%, #f5f0ff 100%)'
          : 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 50%, rgba(102, 126, 234, 0.1) 100%)',
        padding: '32px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '16px',
          overflow: 'hidden',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}>
          <CachedImage
            src={agent.img_url}
            alt={`${agent.title} icon`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            fallback={
              <div style={{
                width: '100%',
                height: '100%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <span style={{ color: 'white', fontWeight: 600, fontSize: '22px' }}>
                  {agent.title.charAt(0).toUpperCase()}
                </span>
              </div>
            }
          />
        </div>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '700',
          color: colors.textPrimary,
          lineHeight: '1.3',
          margin: 0,
        }}>
          {agent.title}
        </h3>
      </div>

      {/* Content area */}
      <div style={{ padding: '20px 24px 24px', display: 'flex', flexDirection: 'column', gap: '16px', flex: 1 }}>
        {/* Description */}
        <p style={{
          fontSize: '13px',
          color: colors.textMuted,
          lineHeight: '1.65',
          margin: 0,
        }}>
          {agent.description}
        </p>

        {/* Metadata */}
        {(agent.typical_audience || agent.access) && (
          <div style={{
            display: 'flex',
            gap: '16px',
            borderTop: `1px solid ${colors.border}`,
            paddingTop: '16px',
            marginTop: 'auto',
          }}>
            {agent.typical_audience && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '10px',
                  fontWeight: '700',
                  color: colors.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  marginBottom: '4px',
                }}>
                  Audience
                </div>
                <div style={{
                  fontSize: '12px',
                  color: colors.textSecondary || colors.textMuted,
                  lineHeight: '1.5',
                }}>
                  {agent.typical_audience}
                </div>
              </div>
            )}
            {agent.access && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '10px',
                  fontWeight: '700',
                  color: colors.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  marginBottom: '4px',
                }}>
                  Access
                </div>
                <div style={{
                  fontSize: '12px',
                  color: colors.textSecondary || colors.textMuted,
                  lineHeight: '1.5',
                }}>
                  {agent.access}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <ConversationSidebar
        isOpen={isSidebarOpen}
        onToggle={toggleSidebar}
        onSelectConversation={handleSelectConversation}
        currentConversationId={undefined}
        refreshTrigger={0}
      />

      <div style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.background,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        overflow: 'hidden',
        marginLeft: isSidebarOpen ? '320px' : '60px',
        transition: 'margin-left 0.3s ease-in-out'
      }}>
        <Header hideTitle />

        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '80px 40px 40px 40px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>

          {isLoading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '50vh',
            }}>
              <div style={{ color: colors.textMuted, fontSize: '16px' }}>Loading agents...</div>
            </div>
          ) : error ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '50vh',
            }}>
              <div style={{ color: colors.errorHover, fontSize: '16px' }}>{error}</div>
            </div>
          ) : (
            <div style={{ maxWidth: '1200px', width: '100%' }}>
              {/* Header */}
              <div style={{
                textAlign: 'center',
                marginBottom: '48px',
              }}>
                <h1 style={{
                  fontSize: '48px',
                  fontWeight: '700',
                  color: colors.textPrimary,
                  marginBottom: '16px',
                  letterSpacing: '-0.025em'
                }}>
                  Agents
                </h1>
                <p style={{
                  fontSize: '18px',
                  color: colors.textMuted,
                  lineHeight: '1.6',
                  margin: 0,
                  maxWidth: '600px',
                  marginLeft: 'auto',
                  marginRight: 'auto'
                }}>
                  Discover custom versions of Chat VNT that combine skills, extra knowledge, and any combination of instructions.
                </p>
              </div>

              {/* Search & Filters */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '20px',
                marginBottom: '36px',
                flexWrap: 'wrap',
              }}>
                {/* Search input */}
                <div style={{ position: 'relative', width: '340px', flexShrink: 0 }}>
                  <svg
                    width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke={colors.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }}
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search agents..."
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 16px 10px 44px',
                      fontSize: '15px',
                      border: `1px solid ${colors.border}`,
                      borderRadius: '12px',
                      backgroundColor: colors.surface,
                      color: colors.textPrimary,
                      outline: 'none',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s ease',
                    }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = colors.borderHover; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = colors.border; }}
                  />
                </div>

                {/* Category pills */}
                {categories.length > 0 && (
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <button
                      onClick={() => setSelectedCategory(null)}
                      style={{
                        padding: '8px 20px',
                        borderRadius: '22px',
                        fontSize: '15px',
                        fontWeight: selectedCategory === null ? '600' : '400',
                        border: `1px solid ${selectedCategory === null ? colors.borderHover : colors.border}`,
                        backgroundColor: selectedCategory === null ? (theme === 'light' ? '#eef2ff' : 'rgba(102, 126, 234, 0.2)') : colors.surface,
                        color: selectedCategory === null ? colors.textPrimary : colors.textMuted,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      All
                    </button>
                    {categories.map((cat) => (
                      <button
                        key={cat}
                        onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
                        style={{
                          padding: '8px 20px',
                          borderRadius: '22px',
                          fontSize: '15px',
                          fontWeight: selectedCategory === cat ? '600' : '400',
                          border: `1px solid ${selectedCategory === cat ? colors.borderHover : colors.border}`,
                          backgroundColor: selectedCategory === cat ? (theme === 'light' ? '#eef2ff' : 'rgba(102, 126, 234, 0.2)') : colors.surface,
                          color: selectedCategory === cat ? colors.textPrimary : colors.textMuted,
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {sortedCategories.map((category) => (
                <div key={category} style={{ marginBottom: '48px' }}>
                  <h2 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: colors.textPrimary,
                    marginBottom: '20px'
                  }}>
                    {category}
                  </h2>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '20px',
                  }}>
                    {agentsByCategory[category]
                      .sort((a, b) => a.sort_order - b.sort_order)
                      .map((agent) => (
                        <AgentCard key={agent.id} agent={agent} />
                      ))}
                  </div>
                </div>
              ))}

              {agents.length === 0 && !isLoading && (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: colors.textMuted
                }}>
                  {(searchQuery || selectedCategory)
                    ? 'No agents match your filters.'
                    : 'Agents Coming Soon'}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AgentsPage;
